import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiPlus } from "react-icons/fi";

import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import LeaveStats from "./components/LeaveStats";
import LeaveFilters from "./components/LeaveFilters";
import LeaveRequestTable from "./components/LeaveRequestTable";
import HrmsFilters from '../../components/hrms/HrmsFilterPanel';
import { matchesHrmsFilters } from '../../components/hrms/hrmsFilterUtils';

const STATUS_LABELS = {
  PENDING: "Pending",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  CANCELLED: "Cancelled",
};

function indiaTodayKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value;
  const month = parts.find((part) => part.type === "month")?.value;
  const day = parts.find((part) => part.type === "day")?.value;

  return `${year}-${month}-${day}`;
}

export default function LeaveRequests() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [requests, setRequests] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [leaveType, setLeaveType] = useState("All");
  const [status, setStatus] = useState("All");
  const [hrmsFilters, setHrmsFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [updatingRequestId, setUpdatingRequestId] = useState("");
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!accessToken) return;

    setIsLoading(true);
    setError("");

    try {
      const [pending, approved, rejected, cancelled, types] = await Promise.all([
        apiRequest("/leave/requests?status=PENDING", { accessToken }),
        apiRequest("/leave/requests?status=APPROVED", { accessToken }),
        apiRequest("/leave/requests?status=REJECTED", { accessToken }),
        apiRequest("/leave/requests?status=CANCELLED", { accessToken }),
        apiRequest("/leave/types?activeOnly=true", { accessToken }),
      ]);

      const allRequests = [...pending, ...approved, ...rejected, ...cancelled].map(
        (request) => ({
          ...request,
          displayStatus: STATUS_LABELS[request.status] || request.status,
          name: request.employee?.fullName || "Unknown Employee",
          employeeCode: request.employee?.employeeCode || "—",
          designation:
            request.employee?.assignments?.[0]?.designation?.name ||
            request.employee?.assignments?.[0]?.department?.name ||
            "Unassigned",
          leaveTypeName: request.leaveType?.name || "Leave",
          startDate: request.startDate?.slice(0, 10),
          endDate: request.endDate?.slice(0, 10),
          totalDays: Number(request.totalDays || 0),
        }),
      );

      setRequests(allRequests);
      setLeaveTypes(types || []);
    } catch (requestError) {
      setRequests([]);
      setError(requestError.message || "Unable to load leave requests.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleStatusChange = async (requestId, action) => {
    const actionLabel = action === "approve" ? "approve" : "reject";

    const confirmed = window.confirm(
      `Are you sure you want to ${actionLabel} this leave request?`,
    );

    if (!confirmed) return;

    setUpdatingRequestId(requestId);
    setError("");

    try {
      await apiRequest(`/leave/requests/${requestId}/${action}`, {
        method: "POST",
        body: {},
        accessToken,
      });

      await loadData();
    } catch (requestError) {
      setError(
        requestError.message || `Unable to ${actionLabel} the leave request.`,
      );
    } finally {
      setUpdatingRequestId("");
    }
  };

  const filteredRequests = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return requests.filter((request) => {
      const matchesSearch =
        !normalizedSearch ||
        request.name.toLowerCase().includes(normalizedSearch) ||
        request.employeeCode.toLowerCase().includes(normalizedSearch);

      const matchesLeaveType =
        leaveType === "All" || request.leaveTypeId === leaveType;

      const matchesStatus =
        status === "All" || request.displayStatus === status;

      return matchesHrmsFilters(request, hrmsFilters) && matchesSearch && matchesLeaveType && matchesStatus;
    });
  }, [requests, search, leaveType, status, hrmsFilters]);

  const stats = useMemo(() => {
    const today = indiaTodayKey();

    return {
      pending: filteredRequests.filter((request) => request.status === "PENDING")
        .length,

      approved: filteredRequests.filter((request) => request.status === "APPROVED")
        .length,

      rejected: filteredRequests.filter((request) => request.status === "REJECTED")
        .length,

      onLeaveToday: filteredRequests.filter(
        (request) =>
          request.status === "APPROVED" &&
          request.startDate <= today &&
          request.endDate >= today,
      ).length,
    };
  }, [filteredRequests]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Leave Management</h1>

          <p className="mt-2 text-gray-500">
            Review and manage employee leave requests.
          </p>
        </div>

        <button
          onClick={() => navigate("/admindashboard/leave/apply")}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-800 text-white font-medium hover:bg-primary-900 transition"
        >
          <FiPlus />
          Apply Leave
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <LeaveStats title="Pending Requests" count={stats.pending} />
        <LeaveStats title="Approved" count={stats.approved} />
        <LeaveStats title="Rejected" count={stats.rejected} />
        <LeaveStats title="On Leave Today" count={stats.onLeaveToday} />
      </div>

      <LeaveFilters
        search={search}
        setSearch={setSearch}
        leaveType={leaveType}
        setLeaveType={setLeaveType}
        status={status}
        setStatus={setStatus}
        leaveTypes={leaveTypes}
      />

      <HrmsFilters records={requests} value={hrmsFilters} onChange={setHrmsFilters} />
      <LeaveRequestTable
        requests={filteredRequests}
        isLoading={isLoading}
        updatingRequestId={updatingRequestId}
        onApprove={(requestId) => handleStatusChange(requestId, "approve")}
        onReject={(requestId) => handleStatusChange(requestId, "reject")}
      />
    </div>
  );
}
